/**
 * @fileoverview Validador de códigos INE municipales para PTEL
 * @module services/validation/INEValidator
 * @version 1.0.0
 * 
 * Detecta discrepancias entre el municipio esperado (documento PTEL)
 * y el municipio devuelto por geocodificadores externos.
 * 
 * Caso típico: CartoCiudad devuelve "Colomers" (Girona, 17055)
 * cuando buscamos "Colomera" (Granada, 18051) → 750 km de error.
 * 
 * @example
 * ```typescript
 * const result = validateINEMatch('18051', '17055');
 * if (!result.isValid) {
 *   console.log(result.error); // INEMismatchError
 * }
 * ```
 */

import { CENTROIDES_MUNICIPIOS } from '../../lib/municipiosCentroides';
import type { INEMismatchError, ErrorContext } from '../../types/errors/GeocodingErrors';

// ============================================================================
// CONSTANTES
// ============================================================================

/**
 * Prefijos de códigos INE para provincias andaluzas
 * Formato: 2 primeros dígitos del código INE de 5 dígitos
 */
export const ANDALUSIAN_PROVINCE_CODES = {
  '04': 'Almería',
  '11': 'Cádiz',
  '14': 'Córdoba',
  '18': 'Granada',
  '21': 'Huelva',
  '23': 'Jaén',
  '29': 'Málaga',
  '41': 'Sevilla',
} as const;

export type AndalusianProvinceCode = keyof typeof ANDALUSIAN_PROVINCE_CODES;

/**
 * Umbrales de distancia para clasificación de errores
 */
export const DISTANCE_THRESHOLDS = {
  /** Municipios limítrofes típicamente <50 km */
  ADJACENT_MAX_KM: 50,
  /** Error menor, posible confusión local */
  MINOR_ERROR_KM: 100,
  /** Error significativo, provincia diferente */
  SIGNIFICANT_ERROR_KM: 300,
  /** Error crítico, comunidad diferente */
  CRITICAL_ERROR_KM: 500,
} as const;

// ============================================================================
// TIPOS
// ============================================================================

export interface MunicipalityData {
  codigo: string;
  nombre: string;
  provincia: string;
  centroide: { x: number; y: number };
}

export interface INEValidationResult {
  isValid: boolean;
  expectedINE: string;
  returnedINE: string;
  distanceKm?: number;
  error?: INEMismatchError;
}

// ============================================================================
// FUNCIONES PRINCIPALES
// ============================================================================

/**
 * Verifica si un código INE pertenece a un municipio andaluz
 * @param code Código INE de 5 dígitos
 * @returns true si es andaluz, false en caso contrario
 */
export function isAndalusianINE(code: string): boolean {
  if (!code || code.length < 2) return false;
  
  const provinceCode = code.substring(0, 2);
  return provinceCode in ANDALUSIAN_PROVINCE_CODES;
}

/**
 * Obtiene el nombre de la provincia a partir del código INE
 * @param code Código INE de 5 dígitos
 * @returns Nombre de la provincia o undefined
 */
export function getProvinceFromINE(code: string): string | undefined {
  if (!code || code.length < 2) return undefined;
  
  const provinceCode = code.substring(0, 2) as AndalusianProvinceCode;
  return ANDALUSIAN_PROVINCE_CODES[provinceCode];
}

/**
 * Obtiene datos completos de un municipio por código INE
 * @param code Código INE de 5 dígitos
 * @returns Datos del municipio o null si no existe
 */
export function getMunicipalityByINE(code: string): MunicipalityData | null {
  const normalizedCode = code.padStart(5, '0');
  const centroide = CENTROIDES_MUNICIPIOS[normalizedCode];
  
  if (!centroide) return null;
  
  return {
    codigo: normalizedCode,
    nombre: centroide.nombre,
    provincia: centroide.provincia,
    centroide: { x: centroide.x, y: centroide.y },
  };
}

/**
 * Calcula la distancia en kilómetros entre dos municipios usando sus centroides
 * @param ine1 Código INE del primer municipio
 * @param ine2 Código INE del segundo municipio
 * @returns Distancia en km o null si algún código no existe
 */
export function calculateINEDistance(ine1: string, ine2: string): number | null {
  const mun1 = getMunicipalityByINE(ine1);
  const mun2 = getMunicipalityByINE(ine2);
  
  if (!mun1 || !mun2) return null;
  
  // Distancia euclidiana en UTM (metros) convertida a km
  const dx = mun1.centroide.x - mun2.centroide.x;
  const dy = mun1.centroide.y - mun2.centroide.y;
  const distanceMeters = Math.sqrt(dx * dx + dy * dy);
  
  return distanceMeters / 1000;
}

/**
 * Valida que el código INE devuelto corresponde al esperado
 * Si difieren, genera un INEMismatchError con distancia calculada
 * 
 * @param expectedINE Código INE esperado (del documento PTEL)
 * @param returnedINE Código INE devuelto (del geocodificador)
 * @param context Contexto opcional para el error
 * @returns Resultado de validación con posible error
 */
export function validateINEMatch(
  expectedINE: string,
  returnedINE: string,
  context?: Partial<ErrorContext>
): INEValidationResult {
  const normalizedExpected = expectedINE.padStart(5, '0');
  const normalizedReturned = returnedINE.padStart(5, '0');
  
  // Coincidencia exacta → válido
  if (normalizedExpected === normalizedReturned) {
    return {
      isValid: true,
      expectedINE: normalizedExpected,
      returnedINE: normalizedReturned,
    };
  }
  
  // Discrepancia detectada → calcular distancia y generar error
  const distanceKm = calculateINEDistance(normalizedExpected, normalizedReturned);
  const expectedMun = getMunicipalityByINE(normalizedExpected);
  const returnedMun = getMunicipalityByINE(normalizedReturned);
  
  const error: INEMismatchError = {
    kind: 'ine_mismatch',
    severity: 'degraded',
    context: {
      timestamp: new Date(),
      source: context?.source ?? 'cartociudad',
      level: context?.level ?? 'L3',
      attemptNumber: context?.attemptNumber ?? 1,
      municipio: expectedMun?.nombre,
      codigoINE: normalizedExpected,
      provincia: expectedMun?.provincia,
      ...context,
    } as ErrorContext,
    expectedINE: normalizedExpected,
    returnedINE: normalizedReturned,
    expectedMunicipality: expectedMun?.nombre ?? 'Desconocido',
    returnedMunicipality: returnedMun?.nombre ?? 'Desconocido (no andaluz)',
    discrepancyKm: distanceKm ?? -1,
    message: buildErrorMessage(
      normalizedExpected,
      normalizedReturned,
      expectedMun,
      returnedMun,
      distanceKm
    ),
  };
  
  return {
    isValid: false,
    expectedINE: normalizedExpected,
    returnedINE: normalizedReturned,
    distanceKm: distanceKm ?? undefined,
    error,
  };
}

/**
 * Clasifica la severidad del error según la distancia
 */
export function classifyDiscrepancy(distanceKm: number): 'adjacent' | 'minor' | 'significant' | 'critical' {
  if (distanceKm <= DISTANCE_THRESHOLDS.ADJACENT_MAX_KM) return 'adjacent';
  if (distanceKm <= DISTANCE_THRESHOLDS.MINOR_ERROR_KM) return 'minor';
  if (distanceKm <= DISTANCE_THRESHOLDS.SIGNIFICANT_ERROR_KM) return 'significant';
  return 'critical';
}

// ============================================================================
// FUNCIONES AUXILIARES
// ============================================================================

function buildErrorMessage(
  expectedINE: string,
  returnedINE: string,
  expectedMun: MunicipalityData | null,
  returnedMun: MunicipalityData | null,
  distanceKm: number | null
): string {
  const expectedName = expectedMun 
    ? `${expectedMun.nombre} (${expectedMun.provincia})`
    : `INE ${expectedINE}`;
  
  const returnedName = returnedMun
    ? `${returnedMun.nombre} (${returnedMun.provincia})`
    : `INE ${returnedINE} (fuera de Andalucía)`;
  
  const distanceStr = distanceKm !== null
    ? ` — distancia: ${distanceKm.toFixed(1)} km`
    : '';
  
  return `Discrepancia INE: esperado ${expectedName}, devuelto ${returnedName}${distanceStr}`;
}

// ============================================================================
// EXPORTACIÓN POR DEFECTO
// ============================================================================

export default {
  isAndalusianINE,
  getProvinceFromINE,
  getMunicipalityByINE,
  calculateINEDistance,
  validateINEMatch,
  classifyDiscrepancy,
  ANDALUSIAN_PROVINCE_CODES,
  DISTANCE_THRESHOLDS,
};
